<template>
  <ModalDialog id="challengeModal" @modal-close="closeModal()">
    <div class="dossier">
      <header class="dossier-header">
        <div>
          <span class="eyebrow">{{ primaryCategory }}</span>
          <h2>{{ challenge.title }}</h2>
        </div>
        <button
          id="close"
          class="button close-button"
          @click="closeModal()"
        >
          Close
        </button>
      </header>

      <div class="meta-strip">
        <div class="meta-box">
          <span class="meta-label">Score</span>
          <strong>{{ $store.getters.challScore(challenge.id) }}</strong>
        </div>
        <div class="meta-box">
          <span class="meta-label">Solves</span>
          <strong>{{ challenge.solves }}</strong>
        </div>
        <div class="meta-box">
          <span class="meta-label">Access</span>
          <strong>{{ accessState }}</strong>
        </div>
      </div>

      <div class="dossier-grid">
        <section class="intel-panel">
          <h3>Intel</h3>
          <div class="intel-body">
            <p v-html="challenge.description" /> <!-- eslint-disable-line vue/no-v-html -->
          </div>
        </section>

        <aside class="support-panel">
          <div class="panel-block">
            <h4>Classification</h4>
            <ul class="tag-list">
              <li v-for="category in challenge.categories" :key="category">
                {{ category }}
              </li>
            </ul>
          </div>

          <div class="panel-block">
            <h4>Authors</h4>
            <p class="stacked">
              <span
                v-for="author in challenge.authors"
                :key="author"
                class="line-item"
              >{{ author }}</span>
            </p>
          </div>

          <div v-if="files.length" class="panel-block">
            <h4>Files</h4>
            <p class="stacked">
              <a
                v-for="file in files"
                :key="file.url"
                :href="file.url"
                target="_blank"
                class="line-item"
              >{{ file.name }}</a>
            </p>
          </div>

          <div v-if="challenge.services && challenge.services.length" class="panel-block">
            <h4>Services</h4>
            <p class="stacked">
              <template v-for="service in challenge.services" :key="service.user_display">
                <a
                  v-if="service.hyperlink"
                  :href="service.user_display"
                  target="_blank"
                  class="line-item"
                >{{ service.user_display }}</a>
                <code v-else class="line-item">{{ service.user_display }}</code>
              </template>
            </p>
          </div>

          <div v-if="challenge.top_solved_teams.length" class="panel-block">
            <h4>First Teams</h4>
            <p class="stacked">
              <span
                v-for="(team, index) in challenge.top_solved_teams"
                :key="team.name"
                :title="teamTooltip(team)"
                class="line-item"
              >{{ index + 1 }}. {{ team.name }}</span>
            </p>
          </div>
        </aside>
      </div>

      <template v-if="!$store.state.archived">
        <div v-if="!$store.state.user" class="flag-state disabled">
          Log in to submit flags
        </div>
        <div v-else-if="!$store.state.team" class="flag-state disabled">
          Create or join a team to submit flags
        </div>
        <div v-else-if="challenge.id in $store.state.team.solves" class="flag-state solved">
          Location cleared
        </div>
        <div v-else class="flag-entry">
          <input
            v-model="flag"
            class="textbox flag-input"
            type="text"
            placeholder="Enter flag here..."
            @keyup.enter="submitFlag"
          >
          <button class="button" @click="submitFlag">
            Submit
          </button>
        </div>
      </template>
    </div>
  </ModalDialog>
</template>

<script>
import { mapState } from 'vuex'
import ModalDialog from '@/components/ModalDialog.vue'
import networking from '@/networking.js'
import { dateToISO } from '@/utilityFunctions.js'

export default {
    components: {
        ModalDialog,
    },
    data () {
        return {
            flag: '',
        }
    },
    computed: {
        files () {
            const files = []
            this.challenge.file_urls.forEach(f => {
                const parts = f.split('/')
                files.push({
                    url: f,
                    name: parts[parts.length - 1],
                })
            })
            return files
        },
        primaryCategory () {
            return this.challenge.categories[0] || 'Target'
        },
        accessState () {
            if (this.$store.state.team && this.challenge.id in this.$store.state.team.solves) return 'Cleared'
            return 'Live'
        },
        ...mapState({
            challenge: state => state.modals.challengeModal.challenge,
        }),
    },
    watch: {
        '$store.state.modals.challengeModal.visible' (current) {
            if (current) {
                this.flag = ''
            }
        },
    },
    methods: {
        async submitFlag () {
            if (!this.flag) return
            const r = await networking.request('POST', '/api/submit_flag', {
                challenge_id: this.challenge.id,
                flag: this.flag,
            })
            if (r.status !== 200) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: `Backend could not be reached: ${r.status}`,
                    body: 'Please try again.',
                })
                return
            }
            const data = await r.json()
            if (!data.success) {
                const messages = []
                for (const field in data.errors) {
                    for (const error in data.errors[field]) {
                        if (field === '__all__') {
                            messages.push(data.errors[field][error].message)
                        } else {
                            messages.push(field + ': ' + data.errors[field][error].message)
                        }
                    }
                }
                this.$store.commit('createToast', {
                    type: 'error',
                    title: messages.join('\n\n'),
                })
                return
            }
            if (!data.correct) {
                this.$store.commit('createToast', {
                    type: 'error',
                    title: 'Incorrect flag',
                })
                return
            }
            this.$store.commit('createToast', {
                type: 'success',
                title: 'Challenge solved!',
            })
            if (data.next_challenge) {
                this.$store.commit('processChallenge', data.next_challenge)
            }
            this.$store.commit('updateUserTeam', data.team)
        },
        closeModal () {
            this.$router.push({ name: 'challenges' })
        },
        teamTooltip (team) {
            return 'Solved at ' + dateToISO(new Date(team.time * 1000))
        },
    },
}
</script>

<style lang="scss" scoped>
.dossier {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.dossier-header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-start;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    color: $color-muted-text;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.78rem;
}

h2 {
    margin: 0;
}

.close-button {
    flex: 0 0 auto;
}

.meta-strip {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin: 1rem 0;
}

.meta-box {
    padding: 0.75rem;
    background-color: $color-surface;
    border-left: 0.25rem solid $challenge-color;
}

.meta-label {
    display: block;
    color: $color-muted-text;
    font-size: 0.78rem;
    text-transform: uppercase;
}

.meta-box strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.15rem;
}

.dossier-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.5fr) minmax(18rem, 1fr);
    gap: 1rem;
    min-height: 0;
    flex: 1 1 auto;
}

.intel-panel,
.panel-block {
    background-color: $color-surface;
}

.intel-panel {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.intel-panel h3,
.panel-block h4 {
    margin-bottom: 0.75rem;
}

.intel-body {
    overflow: auto;
    min-height: 0;
}

.support-panel {
    display: grid;
    gap: 0.75rem;
    align-content: start;
}

.panel-block {
    padding: 0.85rem;
}

.tag-list {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0;
    padding: 0;
}

.tag-list li {
    padding: 0.15rem 0.45rem;
    background-color: $color-body-background;
    border-radius: 0.2rem;
}

.stacked {
    margin: 0;
}

.line-item {
    display: block;
    margin-bottom: 0.35rem;
}

.line-item:last-child {
    margin-bottom: 0;
}

.flag-entry {
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
}

.flag-input {
    flex: 1 1 auto;
    height: 2.5rem;
    padding: 0 0.75rem;
}

.flag-state {
    margin-top: 1rem;
    padding: 0.85rem 1rem;
    font-weight: bold;
    border-radius: 0.2rem;
}

.disabled {
    background-color: $color-neutral;
}

.solved {
    background-color: $color-success;
    color: $color-black;
}

p {
    margin: 0;
    white-space: pre-wrap;
}

@media only screen and (max-width: 990px) {
    .dossier-grid {
        grid-template-columns: 1fr;
    }

    .meta-strip {
        grid-template-columns: 1fr;
    }

    .flag-entry {
        flex-direction: column;
    }
}
</style>
